import { cn } from "@/lib/utils";
import { HOT_SHOTS_STAGES, type BallLevel } from "@/shared/hot-shots-stages";

interface KidProfileCardProps {
  firstName: string;
  lastName: string;
  ballLevel: string | null;
  points: number;
}

const BALL_LEVEL_AVATAR_COLORS: Record<BallLevel, string> = {
  red: "from-red-400 to-red-600",
  orange: "from-orange-400 to-orange-600",
  green: "from-green-400 to-green-600",
  yellow: "from-yellow-400 to-yellow-500",
};

export function KidProfileCard({
  firstName,
  lastName,
  ballLevel,
  points,
}: KidProfileCardProps) {
  const level = ballLevel as BallLevel | null;
  const stage = level ? HOT_SHOTS_STAGES[level] : null;
  const avatarGradient =
    level && BALL_LEVEL_AVATAR_COLORS[level]
      ? BALL_LEVEL_AVATAR_COLORS[level]
      : "from-teal-400 to-emerald-600";

  const initials = `${firstName[0] ?? ""}${lastName[0] ?? ""}`.toUpperCase();

  return (
    <div className="flex flex-col items-center gap-5 px-6 pt-8 pb-6">
      {/* Avatar */}
      <div
        className={cn(
          "w-24 h-24 rounded-full bg-gradient-to-br flex items-center justify-center shadow-lg",
          avatarGradient
        )}
      >
        <span className="text-3xl font-bold text-white">{initials}</span>
      </div>

      {/* Name */}
      <div className="text-center">
        <h1 className="text-3xl font-bold text-gray-900">
          Hey {firstName}! 👋
        </h1>
      </div>

      {/* Hot Shots stage */}
      {stage ? (
        <div
          className={cn(
            "flex items-center gap-2 px-4 py-2 rounded-full border-2 font-semibold text-sm",
            stage.bgColor,
            stage.borderColor,
            stage.color
          )}
        >
          <span className="text-lg">{stage.emoji}</span>
          <span>{stage.label} Player</span>
        </div>
      ) : (
        <div className="flex items-center gap-2 px-4 py-2 rounded-full border-2 border-gray-200 bg-gray-50 text-gray-500 text-sm font-semibold">
          <span>🎾</span>
          <span>Tennis Player</span>
        </div>
      )}

      {/* Points */}
      <div className="flex items-center gap-2 bg-amber-50 border-2 border-amber-200 rounded-2xl px-6 py-3">
        <span className="text-2xl">⭐</span>
        <div className="text-center">
          <p className="text-2xl font-bold text-amber-700">{points}</p>
          <p className="text-xs font-semibold text-amber-600 uppercase tracking-wide">
            points
          </p>
        </div>
      </div>
    </div>
  );
}
